
import { Link } from "react-router-dom";
import { Flame, CheckCircle, ArrowRight, Users, BarChart, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const features = [
  {
    title: "AI-Powered Voice Calls",
    description: "Engage cold leads with natural-sounding AI voice calls that capture attention and gather valuable insights.",
    icon: Phone
  },
  {
    title: "Real-time Insights",
    description: "Track call progress, review transcripts, and get AI-powered summaries of customer intent.",
    icon: BarChart
  },
  {
    title: "Lead Reactivation",
    description: "Identify and prioritize reactivated leads to maximize your sales team's efficiency.",
    icon: Users
  },
  {
    title: "Simple Setup",
    description: "Start making calls within minutes of setup with our intuitive onboarding and lead import tools.",
    icon: MessageCircle
  }
];

const Index = () => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto py-4 px-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Flame className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold">Reheat</span>
          </div>
          <div className="space-x-4">
            <Link to="/onboarding">
              <Button variant="outline" className="rounded-lg">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="flex-grow flex flex-col items-center justify-center py-12 px-4 md:py-24 text-center bg-white">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-gray-900">
            Reactivate Cold Leads with AI Voice Technology
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Reheat helps auto dealerships engage cold leads, gather insights, and identify sales opportunities with AI-powered voice calls.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/onboarding">
              <Button size="lg" className="gap-2 rounded-lg bg-primary hover:bg-primary/90 px-8 py-6 h-auto text-base">
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">How Reheat Works</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow flex">
                <feature.icon className="h-6 w-6 text-primary mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 text-center bg-white">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Ready to Start Reactivating Your Cold Leads?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Set up your dealership profile, upload your leads, and start engaging them today.
          </p>
          <Link to="/onboarding">
            <Button size="lg" className="rounded-lg bg-primary hover:bg-primary/90 px-8 py-6 h-auto text-base">
              Get Started Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 px-4 bg-white">
        <div className="container mx-auto text-center text-gray-500">
          <p>© 2024 Reheat. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
